import React, { ReactNode, useEffect, useState } from "react";
import {
  clearAuthState,
  getAuthState,
  tryRefreshAuthTokens,
  useAuth,
} from "@noom/hive-auth";
import {
  getSessionState,
  updateSessionState,
} from "src/pageDefinitions/session";
import { NoomLoader } from "src/components/noom-loader/NoomLoader";
import { trackEvent } from "./api/tracker";
import { captureMessage } from "@sentry/browser";

export const IDENTITY_SERVER_CALLBACK_PATH = "/p/login/callback";
export const IDENTITY_SERVER_LOGIN_PATH = "/p/login";
const POST_AUTH_PATH = "/p/login/post-auth";

type InternalLocation = Pick<Location, "pathname" | "search" | "hash">;

export type LoginFlow =
  | "checkout"
  | "shopCheckout"
  | "switchAccount"
  | "shopSwitchAccount";

export const POST_AUTH_INTERNAL_LOCATION: InternalLocation = {
  pathname: POST_AUTH_PATH,
  search: "",
  hash: "",
};

declare module "src/pageDefinitions/session" {
  interface BrowserSessionState {
    loginInternalLocation?: InternalLocation;
    loginFlow?: LoginFlow;
    loginRedirectCount?: number;
    loginRedirectTimestamp?: number;
    shopSurveyWritesPending?: boolean;
  }
}

/** Without `returnLocation` the user lands on post-auth, which routes them by account type. */
export function prepareSwitchAccountRedirect(
  returnLocation?: InternalLocation
) {
  updateSessionState("browser", {
    loginInternalLocation: returnLocation
      ? {
          pathname: returnLocation.pathname,
          search: returnLocation.search,
          hash: returnLocation.hash,
        }
      : {
          ...POST_AUTH_INTERNAL_LOCATION,
          search: window.location.search,
        },
    loginFlow: "switchAccount",
    isPostAuthCheckoutSession: undefined,
  });
}

export function redirectToCheckoutLogin() {
  redirectToLogin(
    {
      ...POST_AUTH_INTERNAL_LOCATION,
      search: window.location.search,
    },
    false,
    "checkout"
  );
}

export function getLoginInternalLocation(): InternalLocation | undefined {
  const { loginInternalLocation } = getSessionState("browser");
  return loginInternalLocation;
}

export function getLoginFlow(): LoginFlow | undefined {
  return getSessionState("browser").loginFlow;
}

export function clearLoginFlow() {
  updateSessionState("browser", { loginFlow: undefined });
}

export function clearLoginInternalLocation() {
  updateSessionState("browser", {
    loginInternalLocation: undefined,
  });
}

export function redirectToLogin(
  location: InternalLocation = window.location,
  useReplace = true,
  loginFlow?: LoginFlow
) {
  const loginRedirectCount = getSessionState("browser").loginRedirectCount || 0;
  updateSessionState("browser", {
    loginRedirectCount: loginRedirectCount + 1,
    loginRedirectTimestamp: Date.now(),
    loginInternalLocation: {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    },
    loginFlow,
  });
  const redirectUrl = `${IDENTITY_SERVER_LOGIN_PATH}${window.location.search}`;

  if (useReplace) {
    window.location.replace(redirectUrl);
  } else {
    window.location.href = redirectUrl;
  }
}

/** Shop checkouts live at /shop/<product>/checkout, next to their shopPostAuthAnimation page. */
export function getShopPostAuthAnimationLocation(): InternalLocation {
  return {
    pathname: window.location.pathname.replace(
      /\/checkout\/?$/,
      "/shopPostAuthAnimation"
    ),
    search: window.location.search,
    hash: "",
  };
}

/**
 * Sends a logged-out shop user from checkout to Identity Server; they return to the shopPostAuthAnimation page to finish logging in.
 * With `hasShopSurvey`, the answers from the shop survey are written to the account there.
 */
export function redirectToShopCheckoutLogin({
  hasShopSurvey,
}: {
  hasShopSurvey: boolean;
}) {
  setShopSurveyWritesPending(hasShopSurvey);
  redirectToLogin(getShopPostAuthAnimationLocation(), false, "shopCheckout");
}

/**
 * For a logged-out shop checkout that got a session from another tab: skips
 * Identity Server and reloads into the shopPostAuthAnimation page, so the
 * account loads and the login finishes as after `redirectToShopCheckoutLogin`.
 */
export function reloadIntoShopPostAuthAnimation({
  hasShopSurvey,
}: {
  hasShopSurvey: boolean;
}) {
  setShopSurveyWritesPending(hasShopSurvey);
  const { pathname, search } = getShopPostAuthAnimationLocation();
  window.location.replace(`${pathname}${search}`);
}

function setShopSurveyWritesPending(hasShopSurvey: boolean) {
  updateSessionState("browser", {
    shopSurveyWritesPending: hasShopSurvey || undefined,
  });
}

/** Shop version of `prepareSwitchAccountRedirect`: after logging in with another account, the user returns to the shopPostAuthAnimation page to finish logging in. */
export function prepareShopSwitchAccountRedirect() {
  prepareSwitchAccountRedirect(getShopPostAuthAnimationLocation());
  updateSessionState("browser", {
    loginFlow: "shopSwitchAccount",
    // The next account's stored answers are not from this session's survey.
    shopSurveyWritesPending: undefined,
  });
}

export function hasPendingShopSurveyWrites(): boolean {
  return !!getSessionState("browser").shopSurveyWritesPending;
}

/** Logging in again (e.g. Back into the login pages) then returns to `location`, not the shopPostAuthAnimation page. */
export function finishShopPostAuthAnimation(location: InternalLocation) {
  updateSessionState("browser", {
    shopSurveyWritesPending: undefined,
    loginInternalLocation: {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    },
  });
}

/**
 * A cookie session (`identity_data` / `autologin_data`) stores an empty
 * refresh token. Clearing it does not log the user out: `AutoCookieGuard`
 * logs in again from the same cookie. Same check as hive-auth's
 * `ExpiredTokenGuard`.
 */
function sessionHasRefreshToken(state = getAuthState()) {
  if (!state) {
    return false;
  }
  if (state.useCSAuth && state.coachServer) {
    return state.coachServer.refreshToken.length > 0;
  }
  if (state.sso?.ssoProvider === "IDENTITY_SERVER") {
    return !!state.sso.refreshToken;
  }
  return false;
}

export type LoginRedirectReason =
  | "loggedOut"
  | "sessionExpired"
  | "requestUnauthorized";

export function trackAndRedirectToLogin(reason: LoginRedirectReason) {
  const { loginRedirectTimestamp, loginRedirectCount } =
    getSessionState("browser");
  if (loginRedirectCount) {
    captureMessage("Multiple login redirects detected", "warning");
  }
  trackEvent("BuyflowAuthGuardRedirectToLogin", {
    reason,
    hasRefreshToken: sessionHasRefreshToken(),
    previousLoginRedirectCount: loginRedirectCount ?? 0,
    msSinceLastLoginRedirect: Date.now() - (loginRedirectTimestamp ?? 0),
  });
  redirectToLogin();
}

/**
 * Refreshes an expired session before sending the user to login.
 * Call only when a session exists and shouldReLogin() is true. A logged-out
 * visitor has no token to refresh.
 *
 * With `allowLoggedOut`, a failed refresh of a session that has a refresh
 * token clears the session and reloads the page, so the app starts again as
 * logged out. The app loads user data once, so it must not keep running with
 * the account's data after the session is gone.
 *
 * Returns whether the page is reloading; render a loader until it unloads.
 */
export function useRefreshExpiredSession(
  sessionExpired: boolean,
  { allowLoggedOut = false }: { allowLoggedOut?: boolean } = {}
) {
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    if (!sessionExpired) {
      return undefined;
    }

    let cancelled = false;
    (async () => {
      const canContinueLoggedOut = allowLoggedOut && sessionHasRefreshToken();
      try {
        const refreshed = await tryRefreshAuthTokens();
        if (cancelled || refreshed) {
          return;
        }
        if (canContinueLoggedOut) {
          setReloading(true);
          clearAuthState();
          window.location.reload();
          return;
        }
      } catch {
        if (cancelled) {
          return;
        }
      }
      trackAndRedirectToLogin("sessionExpired");
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionExpired, allowLoggedOut]);

  return reloading;
}

export function AuthGuard({ children }: { children: ReactNode }) {
  const { isAuthenticated, shouldReLogin } = useAuth();
  const sessionExpired = isAuthenticated && shouldReLogin();
  useRefreshExpiredSession(sessionExpired);

  if (!isAuthenticated) {
    trackAndRedirectToLogin("loggedOut");
    return <NoomLoader fullScreen />;
  }
  if (sessionExpired) {
    return <NoomLoader fullScreen />;
  }
  return <>{children}</>;
}
